import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Auth0Context } from "@auth0/auth0-react";
import { base44 } from "@/lib/base44-compat/client";
import { resolveAuthUrl } from "@/lib/auth/auth0Urls";
import { clearAccessTokenGetter, registerAccessTokenGetter } from "@/lib/auth/tokenProvider";
import { setSentryUser } from "@/lib/observability/sentry";

const AuthContext = createContext();
const auth0Audience = (import.meta.env.VITE_AUTH0_AUDIENCE || "").trim();
const hasAuth0Config = Boolean(
  (import.meta.env.VITE_AUTH0_DOMAIN || "").trim() &&
  (import.meta.env.VITE_AUTH0_CLIENT_ID || "").trim()
);
const frontendLogoutUrl = resolveAuthUrl(
  import.meta.env.VITE_AUTH_LOGOUT_URI,
  "/",
);

const toAuthErrorState = (error) => {
  const status = error?.status ?? error?.response?.status;
  if (status === 401) {
    return { type: "auth_required", message: error?.message || "Authentication required" };
  }
  if (status === 403) {
    return { type: "user_not_registered", message: error?.message || "User is not registered" };
  }
  return { type: "unknown", message: error?.message || "Failed to initialize app" };
};

export const AuthProvider = ({ children }) => {
  const auth0 = useContext(Auth0Context);
  const [user, setUser] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [appPublicSettings, setAppPublicSettings] = useState({ id: "local", public_settings: {} });

  const clearImpersonation = useCallback(() => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("impersonated_user_email");
    }
  }, []);

  const syncUser = useCallback(async () => {
    setIsLoadingAuth(true);
    try {
      if (hasAuth0Config) {
        if (auth0.error) {
          setUser(null);
          setSentryUser(null);
          clearImpersonation();
          setAuthError({ type: "unknown", message: auth0.error.message || "Auth0 initialization failed" });
          return;
        }

        if (!auth0.isAuthenticated) {
          setUser(null);
          setSentryUser(null);
          clearImpersonation();
          setAuthError({ type: "auth_required", message: "Authentication required" });
          return;
        }
      }

      const currentUser = await base44.auth.me();
      setUser(currentUser || null);
      setSentryUser(currentUser || null);
      if (!currentUser) {
        clearImpersonation();
      }
      setAuthError(null);
    } catch (error) {
      setUser(null);
      setSentryUser(null);
      clearImpersonation();
      setAuthError(toAuthErrorState(error));
    } finally {
      setIsLoadingAuth(false);
    }
  }, [auth0.error, auth0.isAuthenticated, clearImpersonation]);

  const checkAppState = useCallback(async () => {
    setIsLoadingPublicSettings(true);
    try {
      setAppPublicSettings({ id: "local", public_settings: {} });
      await syncUser();
    } catch (error) {
      setAuthError(toAuthErrorState(error));
    } finally {
      setIsLoadingPublicSettings(false);
    }
  }, [syncUser]);

  useEffect(() => {
    if (!hasAuth0Config) {
      clearAccessTokenGetter();
      return;
    }

    registerAccessTokenGetter(async () => {
      const options = auth0Audience
        ? { authorizationParams: { audience: auth0Audience } }
        : undefined;
      return await auth0.getAccessTokenSilently(options);
    });

    return () => {
      clearAccessTokenGetter();
    };
  }, [auth0.getAccessTokenSilently]);

  useEffect(() => {
    if (hasAuth0Config && auth0.isLoading) {
      setIsLoadingAuth(true);
      setIsLoadingPublicSettings(true);
      return;
    }
    void checkAppState();
  }, [auth0.error, auth0.isAuthenticated, auth0.isLoading, checkAppState]);

  const navigateToLogin = useCallback(async (returnTo = window.location.href) => {
    if (!hasAuth0Config) {
      setAuthError({
        type: "auth_required",
        message: "Auth0 is not configured. Set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID.",
      });
      return;
    }

    const resolvedReturnTo = typeof returnTo === "string" && returnTo.length > 0
      ? returnTo
      : window.location.href;

    const options = {
      appState: { returnTo: resolvedReturnTo },
      ...(auth0Audience ? { authorizationParams: { audience: auth0Audience } } : {}),
    };

    try {
      await auth0.loginWithRedirect(options);
    } catch (error) {
      setAuthError(toAuthErrorState(error));
    }
  }, [auth0.loginWithRedirect]);

  const logout = useCallback(async (shouldRedirect = true) => {
    setUser(null);
    setSentryUser(null);
    setAuthError(null);
    clearImpersonation();

    if (!hasAuth0Config) {
      if (shouldRedirect) {
        window.location.href = frontendLogoutUrl;
      }
      return;
    }

    await auth0.logout(
      shouldRedirect ? { logoutParams: { returnTo: frontendLogoutUrl } } : undefined
    );
  }, [auth0.logout, clearImpersonation]);

  const startImpersonation = useCallback((email) => {
    if (typeof window !== "undefined") {
      localStorage.setItem("impersonated_user_email", email);
      window.location.reload();
    }
  }, []);

  const stopImpersonation = useCallback(() => {
    if (typeof window !== "undefined") {
      clearImpersonation();
      window.location.reload();
    }
  }, [clearImpersonation]);

  const value = useMemo(() => ({
    user,
    isAuthenticated: Boolean(user),
    isLoadingAuth,
    isLoadingPublicSettings,
    authError,
    appPublicSettings,
    logout,
    navigateToLogin,
    checkAppState,
    startImpersonation,
    stopImpersonation,
  }), [
    appPublicSettings,
    authError,
    checkAppState,
    isLoadingAuth,
    isLoadingPublicSettings,
    logout,
    navigateToLogin,
    startImpersonation,
    stopImpersonation,
    user,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
